import buildQuery, { ITEM_ROOT } from "odata-query";

/**
 * Public, library-agnostic filter model. Consumers and the v2 FilterBar depend
 * on THIS shape — never on `odata-query`'s object form — so the serialiser can
 * be swapped without touching call sites.
 */
export type ODataFilter =
    | {
          field: string;
          op: "eq" | "ne" | "ge" | "le" | "gt" | "lt";
          value: string | number | boolean | Date | null;
      }
    | { field: string; op: "contains"; value: string }
    | { field: string; op: "anyEq"; value: string | string[]; path?: string } // nested array lookup (e.g. deviceGroupTags); `path` targets a property of the item (e.g. connectorState)
    | { op: "or" | "and"; filters: ODataFilter[] }
    | { op: "not"; filter: ODataFilter } // negation escape from raw `not (...)`
    | { raw: string }; // customFilterExpression escape hatch

export type ODataOrderBy = { field: string; desc?: boolean };

export type ODataParams = {
    filters?: ODataFilter[]; // top-level array ⇒ AND-joined
    orderby?: ODataOrderBy[];
    select?: string[];
    skip?: number;
    top?: number;
};

/** Map one node of our model onto `odata-query`'s filter input. */
const toOdataQueryFilter = (filter: ODataFilter): string | object => {
    if ("raw" in filter) {
        // odata-query emits string members of a filter array verbatim.
        return filter.raw;
    }

    if ("filters" in filter) {
        return { [filter.op]: filter.filters.map(toOdataQueryFilter) };
    }

    if (filter.op === "not") {
        const inner = toOdataQueryFilter(filter.filter);
        // odata-query only special-cases `not` as a logical operator when its
        // value is an object filter expression. A raw string value (e.g. a
        // `{ raw: ... }` inner filter) is otherwise treated as a literal field
        // named "not" compared via eq — so raw strings must be pre-wrapped as
        // a verbatim `not (...)` expression instead.
        return typeof inner === "string" ? `not (${inner})` : { not: inner };
    }

    if (filter.op === "contains") {
        return {
            [`tolower(${filter.field})`]: {
                contains: filter.value.toLowerCase(),
            },
        };
    }

    if (filter.op === "anyEq") {
        // `path` targets a property of the array item; otherwise compare the
        // item itself (ITEM_ROOT). odata-query prefixes the key with the lambda
        // variable, so a path yields e.g. `connectors/connectorState eq …`.
        const key = filter.path ?? ITEM_ROOT;
        const lambda = Array.isArray(filter.value)
            ? {
                  or: filter.value.map((value) => ({
                      [key]: { eq: value },
                  })),
              }
            : { [key]: { eq: filter.value } };
        return { [filter.field]: { any: lambda } };
    }

    if (filter.op === "eq") {
        return { [filter.field]: filter.value };
    }

    return { [filter.field]: { [filter.op]: filter.value } };
};

/**
 * Serialise our typed model into the `$`-param object the mutator forwards as
 * query string. `odata-query` emits `Date` values as unquoted ISO literals: UTC
 * dates carry a `Z` suffix, while offset-bearing `TZDate`s (e.g. from the NEM
 * date picker) carry a `+hh:mm` offset — which must survive URL encoding, see
 * the `%2B` handling below.
 */
export const toODataParams = (params: ODataParams): Record<string, string> => {
    const queryString = buildQuery({
        filter: params.filters?.length
            ? params.filters.map(toOdataQueryFilter)
            : undefined,
        orderBy: params.orderby?.map(
            (order) => `${order.field}${order.desc ? " desc" : ""}`,
        ),
        select: params.select,
        skip: params.skip,
        top: params.top,
    });

    // buildQuery returns "?$filter=…&$top=…"; convert to a params object so the
    // existing custom-fetch `params` → URLSearchParams path is unchanged.
    //
    // odata-query does NOT percent-encode unquoted literals (e.g. a Date's ISO
    // string), so a positive timezone offset like `+10:00` arrives as a bare
    // `+`. `URLSearchParams` decodes a bare `+` as a space, severing the offset
    // and yielding an invalid DateTimeOffset (`2026-06-14T00:00:00.000`) that the
    // backend rejects. Percent-encode bare `+` to `%2B` first so the offset
    // survives the round-trip. Quoted string literals are already encoded by
    // odata-query, so they contain no bare `+` and are unaffected.
    return Object.fromEntries(
        new URLSearchParams(
            queryString.replace(/^\?/, "").replace(/\+/g, "%2B"),
        ),
    );
};

/**
 * One level of OData navigation from an object-valued (non-array, non-Date)
 * property, e.g. `userAccount/username` on a DTO whose `userAccount` is an
 * object. Mirrors the deprecated DevExtreme `parent/child` search paths.
 */
type NestedSearchField<T> = {
    [K in keyof T & string]: NonNullable<T[K]> extends readonly unknown[] | Date
        ? never
        : NonNullable<T[K]> extends object
          ? `${K}/${keyof NonNullable<T[K]> & string}`
          : never;
}[keyof T & string];

/**
 * A field that can be searched on `T`: any top-level string key, or a one-level
 * nested navigation path into an object-valued property (e.g.
 * `userAccount/username`). Array and `Date` properties are excluded from
 * nesting.
 */
export type SearchField<T> = (keyof T & string) | NestedSearchField<T>;

/**
 * Builds a cross-field search filter from a plain-text search term and a list
 * of OData field paths. Multiple fields are OR-joined with `contains` (which is
 * case-insensitive — see {@link toODataParams}). Returns `undefined` when the
 * text is empty or no fields are provided.
 *
 * @example
 * buildSearchFilter("abc", ["id", "label"])
 * // → { op: "or", filters: [{ field: "id", op: "contains", value: "abc" }, ...] }
 */
export const buildSearchFilter = <T = unknown>(
    text: string,
    fields: readonly SearchField<T>[],
): ODataFilter | undefined => {
    const trimmed = text.trim();
    if (!trimmed || !fields.length) return undefined;

    if (fields.length === 1) {
        return { field: fields[0], op: "contains", value: trimmed };
    }

    return {
        op: "or",
        filters: fields.map((field) => ({
            field,
            op: "contains" as const,
            value: trimmed,
        })),
    };
};
